import { z } from 'zod';
import { ToolEffect, ToolPolicyRule } from './guardrails.js';

/** MCP servers (org-level config) and agent bindings. Credentials are write-only. */

export const MCP_TRANSPORTS = ['http', 'sse', 'stdio'] as const;
export const McpTransport = z.enum(MCP_TRANSPORTS);

/** Programs a stdio MCP server may be started with without admin review. */
export const STDIO_COMMAND_ALLOWLIST: readonly string[] = [
  'npx',
  'uvx',
  'node',
  'python',
  'python3',
  'deno',
  'bun',
  'bunx',
  'pipx',
];

/**
 * `none`, `bearer` (Authorization: Bearer <secret>), `header:<Header-Name>`, or `oauth` (MCP
 * authorization: sign in once, the platform keeps and refreshes the tokens).
 */
export const McpAuthType = z
  .string()
  .max(80)
  .regex(/^(none|bearer|oauth|header:[A-Za-z0-9-]{1,64})$/, 'must be none, bearer, oauth or header:<Name>');

/**
 * Pre-registered OAuth client for `oauth` servers whose authorization server has no dynamic client
 * registration (Google, Slack, HubSpot…). Write-only; stored sealed with the tokens.
 */
export const McpOAuthClientInput = z.object({
  clientId: z.string().trim().min(1).max(500),
  clientSecret: z.string().trim().min(1).max(2000).optional(),
});

/**
 * Which of a server's tools agents may use. `read` (default): only tools the server declares
 * read-only (or an admin marked `read`); `readwrite`: all tools. `tools` switches single tools
 * on/off and wins over the mode. The tool policy still decides allow/ask/deny for allowed tools.
 */
export const McpToolAccess = z.object({
  mode: z.enum(['read', 'readwrite']).default('read'),
  tools: z.record(z.string().max(200), z.boolean()).default({}),
});
export type McpToolAccess = z.infer<typeof McpToolAccess>;
export const DEFAULT_MCP_TOOL_ACCESS: McpToolAccess = { mode: 'read', tools: {} };

/** Declared read-only by the server (or marked `read` by an admin). Drives the `read` access mode. */
export function isReadTool(t: { annotations?: Record<string, unknown> | null; override?: string }): boolean {
  if (t.override) return t.override === 'read';
  return t.annotations?.readOnlyHint === true && t.annotations?.destructiveHint !== true;
}

/** May agents use this tool at all? (Single source for the runtime, the API and the UI.) */
export function isMcpToolAllowed(
  access: McpToolAccess | null | undefined,
  t: { name: string; annotations?: Record<string, unknown> | null; override?: string },
): boolean {
  const a = access ?? DEFAULT_MCP_TOOL_ACCESS;
  const sw = a.tools?.[t.name];
  if (typeof sw === 'boolean') return sw;
  return a.mode === 'readwrite' || isReadTool(t);
}

const McpServerFields = {
  name: z.string().min(1).max(80),
  url: z.string().url().max(2000).optional(),
  command: z
    .string()
    .min(1)
    .max(200)
    .regex(/^[A-Za-z0-9._/@+-]+$/, 'must be a program name or path without spaces or shell characters')
    .optional(),
  args: z
    .array(
      z
        .string()
        .max(500)
        .refine((a) => !a.includes('\u0000'), 'invalid character'),
    )
    .max(50)
    .default([]),
  image: z.string().max(200).optional(),
  authType: McpAuthType.default('none'),
  /** Write-only credential for `bearer` / `header:` auth. Never returned. */
  secret: z.string().min(1).max(4000).optional(),
  /** `oauth` only: a pre-registered client (omit to use dynamic client registration). */
  oauthClient: McpOAuthClientInput.optional(),
  /** Catalog preset id (see MCP_PRESETS); informational. */
  presetId: z.string().max(64).optional(),
  trust: z.enum(['trusted', 'untrusted']).default('untrusted'),
  /** Admin effect overrides per tool name. */
  toolOverrides: z.record(z.string().max(200), ToolEffect).default({}),
  /** Default: read-only. */
  toolAccess: McpToolAccess.optional(),
  enabled: z.boolean().default(true),
};

export const McpServerInput = z
  .object({ ...McpServerFields, transport: McpTransport })
  .superRefine((v, ctx) => {
    if (v.transport === 'stdio' && !v.command)
      ctx.addIssue({ code: 'custom', path: ['command'], message: 'required for stdio servers' });
    if (v.transport !== 'stdio' && !v.url)
      ctx.addIssue({ code: 'custom', path: ['url'], message: 'required for http/sse servers' });
    if (v.authType === 'oauth' && v.transport === 'stdio')
      ctx.addIssue({ code: 'custom', path: ['authType'], message: 'oauth needs an http/sse server' });
    if (v.oauthClient && v.authType !== 'oauth')
      ctx.addIssue({ code: 'custom', path: ['oauthClient'], message: 'only for authType oauth' });
  });
export type McpServerInput = z.infer<typeof McpServerInput>;

export const McpServerPatch = z.object({
  name: McpServerFields.name.optional(),
  url: z.string().url().max(2000).nullable().optional(),
  command: McpServerFields.command.unwrap().nullable().optional(),
  args: McpServerFields.args.unwrap().optional(),
  image: z.string().max(200).nullable().optional(),
  authType: McpAuthType.optional(),
  /** `null` removes the stored credential (for `oauth`: the tokens and client, i.e. disconnect). */
  secret: z.string().min(1).max(4000).nullable().optional(),
  /** `oauth` only: replace the pre-registered client (forces a new sign-in). */
  oauthClient: McpOAuthClientInput.optional(),
  trust: z.enum(['trusted', 'untrusted']).optional(),
  toolOverrides: z.record(z.string().max(200), ToolEffect).optional(),
  toolAccess: McpToolAccess.optional(),
  enabled: z.boolean().optional(),
});
export type McpServerPatch = z.infer<typeof McpServerPatch>;

export const McpServerDto = z.object({
  id: z.string(),
  name: z.string(),
  transport: McpTransport,
  url: z.string().nullable(),
  command: z.string().nullable(),
  args: z.array(z.string()),
  image: z.string().nullable(),
  authType: z.string(),
  /** "••••abcd" when a credential is stored. */
  secretHint: z.string().nullable(),
  presetId: z.string().nullable(),
  /** The server last rejected its credential (any auth type); cleared by a working connection. */
  authFailed: z.boolean(),
  authError: z.string().nullable(),
  /** `oauth` servers only. */
  oauth: z
    .object({
      status: z.enum(['connected', 'needs_auth']),
      error: z.string().nullable(),
      connectedAt: z.string().nullable(),
      /** A pre-registered client is stored (dynamic registration otherwise). */
      hasClient: z.boolean(),
    })
    .nullable(),
  trust: z.enum(['trusted', 'untrusted']),
  toolOverrides: z.record(z.string(), z.string()),
  toolAccess: McpToolAccess,
  enabled: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type McpServerDto = z.infer<typeof McpServerDto>;

export const McpToolInfo = z.object({
  name: z.string(),
  description: z.string().nullable(),
  annotations: z.record(z.string(), z.unknown()).nullable(),
  /** Effect after classification (override > annotations (trust-aware) > `write`). */
  effect: ToolEffect,
  effectSource: z.enum(['override', 'annotations', 'default']),
});
export type McpToolInfo = z.infer<typeof McpToolInfo>;
export const McpDiscoverResult = z.object({
  tools: z.array(McpToolInfo),
  latencyMs: z.number(),
});
export type McpDiscoverResult = z.infer<typeof McpDiscoverResult>;

/** A repository the server's credential can reach (read from the vendor's REST API, not the MCP server). */
export const McpRepo = z.object({
  fullName: z.string(),
  private: z.boolean(),
  archived: z.boolean(),
  description: z.string().nullable(),
  url: z.string(),
  defaultBranch: z.string().nullable(),
  /** Highest permission the credential has on the repo. */
  permission: z.enum(['admin', 'write', 'read']),
  pushedAt: z.string().nullable(),
});
export type McpRepo = z.infer<typeof McpRepo>;
export const McpReposResult = z.object({
  repos: z.array(McpRepo),
  /** More repositories exist than were fetched (page cap). */
  truncated: z.boolean(),
});
export type McpReposResult = z.infer<typeof McpReposResult>;

export const McpOAuthStartInput = z.object({
  /** Opened in a popup window: the callback page notifies the app and closes itself. */
  popup: z.boolean().default(false),
});
export const McpOAuthStartResult = z.object({ authorizeUrl: z.string() });
export type McpOAuthStartResult = z.infer<typeof McpOAuthStartResult>;
export const McpOAuthCallbackQuery = z.object({
  state: z.string().min(16).max(200),
  code: z.string().max(4000).optional(),
  error: z.string().max(200).optional(),
  error_description: z.string().max(1000).optional(),
});

/** Binding of an MCP server to an agent version: tools appear as `mcp.<prefix>.<tool>`. */
export const McpBinding = z.object({
  mcpServerId: z.string().min(1).max(64),
  prefix: z
    .string()
    .min(1)
    .max(24)
    .regex(/^[a-z0-9_]+$/, 'lowercase letters, digits and _ only'),
  /** null = all tools of the server. */
  enabledTools: z.array(z.string().min(1).max(200)).max(200).nullable().default(null),
});
export type McpBinding = z.infer<typeof McpBinding>;

// ---------- Org tool policies ----------
export const ToolPolicyDto = ToolPolicyRule.extend({ id: z.string(), createdAt: z.string() });
export type ToolPolicyDto = z.infer<typeof ToolPolicyDto>;
export const ToolPolicyReplace = z.object({ rules: z.array(ToolPolicyRule).max(200) });

// ---------- ask_human ----------
export const RunAnswerInput = z.object({ answer: z.string().trim().min(1).max(10_000) });

/**
 * Platform tool through which an agent asks for an org MCP server to be bound to it. Always needs a
 * human approval by someone allowed to change agents (agent:update); "always allow" never applies.
 */
export const REQUEST_ACCESS_TOOL = 'request_access';
/**
 * Platform tool through which an agent asks for one more host on its sandbox's egress allowlist. Same
 * approval rules as request_access; approved, it applies to the running run from its next sandbox command.
 */
export const REQUEST_NETWORK_ACCESS_TOOL = 'request_network_access';

/**
 * Umbrella permission (an entry in `agent_versions.builtin_tools`, not a tool itself): an agent that
 * holds it gets the company tools below and can build out the company. Any agent can be granted it;
 * only a human can grant it (agents can never hand it out), and nothing keys off a role name.
 */
export const COMPANY_PERMISSION = 'manage_company';
/**
 * Umbrella permission for trigger management (cron, interval, event) of agents below the holder. Separate
 * from `manage_company` so triggers can be granted without company rights; same rule: only a human grants it.
 */
export const TRIGGER_PERMISSION = 'manage_triggers';
/**
 * Git workspace tools (guardrails.git.workspace = auto): offered with the sandbox file tools when the agent has
 * a GitHub/GitLab connection that may write; `repo_push` only with admin-listed push branches.
 */
/**
 * Outbox event the API writes for workspace actions (the API never talks to the runner; the worker does).
 * Payload: { agentId, action: 'refresh' | 'remove' | 'reset', path?, userId }.
 */
export const WORKSPACE_ACTION_EVENT = 'workspace.action_requested';
/** Notified (in-app) when the platform removes a working copy that still had unpublished work. */
export const WORKSPACE_REMOVED_EVENT = 'workspace.removed';
export const WORKSPACE_ACTIONS = ['refresh', 'remove', 'reset'] as const;
export type WorkspaceAction = (typeof WORKSPACE_ACTIONS)[number];

export const GIT_TOOLS = [
  'repo_checkout',
  'repo_diff',
  'repo_commit',
  'repo_sync',
  'repo_save',
  'repo_publish',
  'repo_checks',
  'repo_push',
] as const;
export const COMPANY_LIST_TOOL = 'company_list_agents';
export const COMPANY_GET_TOOL = 'company_get_agent';
export const COMPANY_CREATE_TOOL = 'company_create_agent';
export const COMPANY_UPDATE_TOOL = 'company_update_agent';
export const REQUEST_BUILTIN_ACCESS_TOOL = 'company_request_builtin_access';
export const UPDATE_COMPANY_FALLBACK_TOOL = 'update_company_fallback';
export const UPDATE_AGENT_FALLBACK_TOOL = 'update_agent_fallback';
export const COMPANY_ARCHIVE_TOOL = 'company_archive_agent';
export const COMPANY_TOOLS = [
  COMPANY_LIST_TOOL,
  COMPANY_GET_TOOL,
  COMPANY_CREATE_TOOL,
  COMPANY_UPDATE_TOOL,
  COMPANY_ARCHIVE_TOOL,
  REQUEST_BUILTIN_ACCESS_TOOL,
] as const;
export const TRIGGER_LIST_TOOL = 'company_list_triggers';
export const TRIGGER_CREATE_TOOL = 'company_create_trigger';
export const TRIGGER_UPDATE_TOOL = 'company_update_trigger';
export const TRIGGER_DELETE_TOOL = 'company_delete_trigger';
export const TRIGGER_TOOLS = [
  TRIGGER_LIST_TOOL,
  TRIGGER_CREATE_TOOL,
  TRIGGER_UPDATE_TOOL,
  TRIGGER_DELETE_TOOL,
] as const;
/** Triggers an agent creates fire at most this often per hour (the human API allows up to 3600). */
export const AGENT_TRIGGER_MAX_FIRES_PER_HOUR = 12;

/**
 * Tools whose approval changes the company/agent configuration: approving needs the listed RBAC
 * permission (+ second factor for owner/admin) and is never remembered as "always allow".
 */
export const PRIVILEGED_APPROVAL_TOOLS: Readonly<
  Record<
    string,
    'agent:create' | 'agent:update' | 'policy:update' | 'trigger:create' | 'trigger:update' | 'trigger:delete'
  >
> = {
  [REQUEST_ACCESS_TOOL]: 'agent:update',
  [REQUEST_NETWORK_ACCESS_TOOL]: 'agent:update',
  [COMPANY_CREATE_TOOL]: 'agent:create',
  [COMPANY_UPDATE_TOOL]: 'agent:update',
  [REQUEST_BUILTIN_ACCESS_TOOL]: 'agent:update',
  [UPDATE_AGENT_FALLBACK_TOOL]: 'agent:update',
  [UPDATE_COMPANY_FALLBACK_TOOL]: 'policy:update',
  [COMPANY_ARCHIVE_TOOL]: 'agent:update',
  [TRIGGER_CREATE_TOOL]: 'trigger:create',
  [TRIGGER_UPDATE_TOOL]: 'trigger:update',
  [TRIGGER_DELETE_TOOL]: 'trigger:delete',
};

/**
 * Approvals for `company_create_agent` / `company_update_agent` that grant permissions the calling
 * agent does not have itself (extra tools, integrations, network, higher limits). The platform appends
 * a section starting with this marker to the approval's `inputPreview` (deterministic, never written by
 * the model); web, notifications and audit recognise such approvals by it.
 */
export const ESCALATION_MARKER = '⚠ EXTRA PERMISSIONS';

/** Is this approval a request for permissions above the requesting agent's own level? */
export const isEscalationApproval = (toolName: string, inputPreview: string): boolean =>
  (toolName === COMPANY_CREATE_TOOL ||
    toolName === COMPANY_UPDATE_TOOL ||
    toolName === REQUEST_BUILTIN_ACCESS_TOOL) &&
  inputPreview.includes(`\n\n${ESCALATION_MARKER}`);

/** Message the requesting agent gets when a human refuses the extra permissions. */
export const escalationDeniedMessage = (toolName: string, humanReason?: string | null): string =>
  `A human refused the extra permissions you requested, so nothing was ${
    toolName === COMPANY_CREATE_TOOL ? 'created' : 'changed'
  }.${humanReason?.trim() ? ` Reason: ${humanReason.trim()}.` : ''} You can try again with only permissions you have yourself (no approval needed), or ask a human.`;

/** Permission needed to approve this tool call, or undefined for ordinary tools. */
export const privilegedApprovalPerm = (toolName: string) =>
  Object.hasOwn(PRIVILEGED_APPROVAL_TOOLS, toolName) ? PRIVILEGED_APPROVAL_TOOLS[toolName] : undefined;
